import { Component, OnInit, QueryList, AfterViewInit, ContentChildren, ChangeDetectorRef, ViewChild, Input, HostListener } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap/';
import { ModalHeaderComponent } from '../modal-header/modal-header.component';
import { ModalWizardPageComponent } from '../modal-wizard-page/modal-wizard-page.component';
import { ScuiModalComponent } from '../modal/modal.component';
import { tap } from 'rxjs/operators';

@Component({
    selector: 'sc-modal-wizard',
    templateUrl: './modal-wizard.component.html',
    styleUrls: ['./modal-wizard.component.scss'],
    standalone: false
})
export class ModalWizardComponent implements OnInit, AfterViewInit {

  @Input()
  size: 'sm' | 'lg' | 'xl' = null;

  @ViewChild('modal')
  modal : ScuiModalComponent;

  @ContentChildren(ModalWizardPageComponent, { descendants: true })
  _pages: QueryList<ModalWizardPageComponent>;

  @ContentChildren(ModalHeaderComponent, { descendants: true }) 
  _headers :  QueryList<ModalHeaderComponent>;

  @ContentChildren(ModalHeaderComponent, { descendants: false })
  _directHeaders :  QueryList<ModalHeaderComponent>;

  currentPage: ModalWizardPageComponent;

  private wiredHeaders = new WeakSet<ModalHeaderComponent>();

  // the page that only holds the height role because it happened to be first
  private autoHeightPage: ModalWizardPageComponent;

  ngOnInit() { }

  ngAfterViewInit() {

    this._directHeaders.changes.subscribe(() => this.updatePageOffsets());

    // pages and headers can come from an *ngFor over data that arrives after the
    // wizard, and re-filtering that data replaces the whole collection, so both
    // have to be picked up whenever they change instead of only once
    this._pages.changes.subscribe(() => this.initPages());
    this._headers.changes.subscribe(() => this.wireHeaders());

    this.wireHeaders();
    this.initPages();
  }

  private wireHeaders() {

    this._headers.forEach(h => {

      if (this.wiredHeaders.has(h)) return;

      this.wiredHeaders.add(h);
      h.closeClick.subscribe(() => this.dismiss());
    });
  }

  private initPages() {

    if (!this._pages.length) {
      this.currentPage = null;
      return;
    }

    // exactly one page stays in flow and gives the wizard its height. A page that
    // declares definesHeight itself keeps it; otherwise the role follows the first
    // page and is handed back when the collection changes.
    var pages = this._pages.toArray();
    var declaredHeightPage = pages.find(p => p.definesHeight && p != this.autoHeightPage);
    var nextAutoHeightPage = declaredHeightPage ? null : pages[0];
    var previousAutoHeightPage = this.autoHeightPage;

    this.autoHeightPage = nextAutoHeightPage;

    if (previousAutoHeightPage != nextAutoHeightPage) {
      setTimeout(() => {
        if (previousAutoHeightPage) previousAutoHeightPage.definesHeight = false;
        if (nextAutoHeightPage) nextAutoHeightPage.definesHeight = true;
      });
    }

    // the page we were on can have been destroyed along with the previous collection
    if (!this._pages.some(p => p == this.currentPage)) {
      this.currentPage = this._pages.first;
    }

    this._pages.forEach(page => page == this.currentPage ? page.show() : page.hide());

    setTimeout(() => this.updatePageOffsets());
  }

  show(targetPage: ModalWizardPageComponent = null) {

    // without a target the wizard starts over: the page it was left on can be a
    // confirmation page, or one that no longer exists in a dynamic collection
    var startPage = targetPage ?? this._pages?.first;

    if (startPage)
    {
      this._pages.forEach(page => {
        if (page == startPage) {
          this.currentPage = page;
          page.show();
        }
        else {
          page.hide();
        }
      });
    }
    
    setTimeout(() => this.updatePageOffsets(), 100);

    return this.modal.show('modal-wizard');
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updatePageOffsets();
  }

  // pages are positioned absolutely (all of them on mobile), so they have to start below
  // every header of the wizard, not just the first one. The offset is ignored by pages
  // that are laid out in flow.
  updatePageOffsets() {

    var offset = 0;

    this._directHeaders.forEach(header => {
      if (header.headerDiv) offset += header.headerDiv.nativeElement.offsetHeight;
    });

    this._pages.forEach(page => page.topOffset = offset + 'px');
  }

  dismiss() {
    this.modal.dismiss();
  }

  complete(args)
  {
    this.modal.complete(args);
  }

  navigateTo(targetPage: ModalWizardPageComponent, direction: 'rightToLeft' | 'leftToRight' | 'auto' | 'none' = 'auto') {

    // headers can render differently per page, so they have to be measured after the
    // caller's state change has been rendered
    setTimeout(() => this.updatePageOffsets());

    var isCurrentPageOut = false;
    var isTargetPageIn = false;

    this._pages.forEach(page => {

      if (page == targetPage) {

        console.log('Target page located');
        console.log(direction);
        console.log(isCurrentPageOut);

        if (direction == 'none')
        {
          page.show();
        }
        else if (direction == 'rightToLeft' || (direction == 'auto' && isCurrentPageOut)) {
          page.slideInFromRight();
        }
        else {
          page.slideInFromLeft();
        }

        isTargetPageIn = true;
      }
      else if (page == this.currentPage)
      {       
        if (direction == 'none')
        {
          page.hide();
        }
        else if (direction == 'rightToLeft' || (direction == 'auto' && !isTargetPageIn)) {
          page.slideOutToLeft();
        }
        else {
          page.slideOutToRight();
        }
        
        isCurrentPageOut = true;
      }
    });

    this.currentPage = targetPage;
  }

  // pages can come from an *ngFor, so callers address them by position
  getPage(index: number)
  {
    return this._pages?.get(index);
  }

  getNextPage()
  {
    var currentPageFound = false;
    var targetPage = null;

    this._pages.forEach(page => {

      if (currentPageFound && targetPage != null)
      {
        return;
      }
      if (currentPageFound)
      {
        targetPage = page;
        return;
      }
      if (page == this.currentPage) {
        currentPageFound = true;        
      }
    });
    
    return targetPage;
  }

  getPreviousPage()
  {    
    var previousPage = null;
    var targetPage = null;

    this._pages.forEach(page => {

      if (targetPage != null)
      {
        return;
      }
      if (page == this.currentPage) {
        targetPage = previousPage;
        return;        
      }

      previousPage = page;
    });
    
    return targetPage;
  }

  next() {    

    var targetPage = this.getNextPage();
    if (targetPage)
      this.navigateTo(targetPage);

  }

  previous() {    

    var targetPage = this.getPreviousPage();
    if (targetPage)
      this.navigateTo(targetPage);

  }

}
